import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCompanyStore, useIsAdmin } from '../store/companyStore';
import type { DropdownOption } from '../types/componentType';
import type { ListaSubestipulanteItem } from '../types/vida/subEstipulante/subEstipulanteType';
import { formatCnpj } from '../utils/cnpjUtils';
import { formatCpf } from '../utils/cpfUtils';

/** Referência estável para o seletor do Zustand (evita novo `[]` a cada snapshot). */
const EMPTY_SUBESTIPULANTES: ListaSubestipulanteItem[] = [];

const SUBESTIPULANTES_PAGE_SIZE = 6;

export const SUBESTIPULANTES_SORT_OPTIONS: DropdownOption[] = [
  { label: 'Nome (A-Z)', value: 'nome' },
  { label: 'Nome (Z-A)', value: 'nome-desc' },
  { label: 'Contrato (menor)', value: 'contrato' },
  { label: 'Contrato (maior)', value: 'contrato-desc' },
  { label: 'Documento (A-Z)', value: 'documento' },
  { label: 'Documento (Z-A)', value: 'documento-desc' },
  { label: 'Status (A-Z)', value: 'status' },
  { label: 'Status (Z-A)', value: 'status-desc' },
];

const LEADING_DIGITS_RE = /^\d*/;

function takeLeadingDigits(value: string): string {
  return LEADING_DIGITS_RE.exec(value)?.[0] ?? '';
}

export function formatVigenciaSubDisplay(value: string | undefined): string {
  if (!value?.trim()) return '—';
  const raw = value.trim();
  const dateOnly = raw.split(/[Tt\s,]/)[0]?.trim() ?? '';
  const parts = dateOnly.split(/[-/.]/);
  if (parts.length >= 3) {
    const [a, b, c] = parts;
    const day = takeLeadingDigits(String(a.length === 4 ? c : a));
    const month = takeLeadingDigits(String(b ?? ''));
    const year = takeLeadingDigits(String(a.length === 4 ? a : c));
    if (day && month && year) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
  }
  if (dateOnly && !/:\d{2}/.test(dateOnly)) {
    return dateOnly;
  }
  return '—';
}

export function formatDocSubestipulante(raw: string | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 14) return formatCnpj(digits);
  if (digits.length === 11) return formatCpf(digits);
  return String(raw ?? '').trim() || '—';
}

function subestipulanteMatchesSearch(
  row: ListaSubestipulanteItem,
  term: string,
  formatDocumento: (raw: string | undefined) => string,
): boolean {
  const t = term.toLowerCase().trim();
  if (!t) return true;
  const nome = (row.nomeSubestipulanteOut ?? '').toLowerCase();
  const docRaw = (row.numDocumentoOut ?? '').toLowerCase();
  const docFmt = formatDocumento(row.numDocumentoOut).toLowerCase();
  const status = (row.dsCstatus ?? '').toLowerCase();
  return nome.includes(t) || docRaw.includes(t) || docFmt.includes(t) || status.includes(t);
}

function sortSubestipulantesList(rows: ListaSubestipulanteItem[], sortBy: string): ListaSubestipulanteItem[] {
  if (!sortBy) return rows;
  const copy = [...rows];
  const cmp = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });
  switch (sortBy) {
    case 'nome':
      return copy.sort((a, b) => cmp(a.nomeSubestipulanteOut ?? '', b.nomeSubestipulanteOut ?? ''));
    case 'nome-desc':
      return copy.sort((a, b) => cmp(b.nomeSubestipulanteOut ?? '', a.nomeSubestipulanteOut ?? ''));
    case 'contrato':
      return copy.sort((a, b) => (a.identContratoOut ?? 0) - (b.identContratoOut ?? 0));
    case 'contrato-desc':
      return copy.sort((a, b) => (b.identContratoOut ?? 0) - (a.identContratoOut ?? 0));
    case 'documento':
      return copy.sort((a, b) => cmp(a.numDocumentoOut ?? '', b.numDocumentoOut ?? ''));
    case 'documento-desc':
      return copy.sort((a, b) => cmp(b.numDocumentoOut ?? '', a.numDocumentoOut ?? ''));
    case 'status':
      return copy.sort((a, b) => cmp(a.dsCstatus ?? '', b.dsCstatus ?? ''));
    case 'status-desc':
      return copy.sort((a, b) => cmp(b.dsCstatus ?? '', a.dsCstatus ?? ''));
    default:
      return copy;
  }
}

export interface UseHomeContentReturn {
  isAdmin: boolean;
  subestipulantes: ListaSubestipulanteItem[];
  mainContentId: string;
  actionsRegionId: string;
  searchInputId: string;
  startDateId: string;
  endDateId: string;
  subestipulantesHeadingId: string;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  isBuscarEnabled: boolean;
  showInvalidDateMessage: boolean;
  handleBuscar: () => void;
  handleNavigateToPoliceholder: () => void;
  subestipulantesPanelId: string;
  subEstSearchInputId: string;
  subestipulantesExpanded: boolean;
  toggleSubestipulantesExpanded: () => void;
  subestipulantesView: 'cards' | 'table';
  toggleSubestipulantesView: () => void;
  subEstSearchTerm: string;
  setSubEstSearchTerm: (value: string) => void;
  subEstSortBy: string;
  setSubEstSortBy: (value: string) => void;
  subestipulantesFilteredSorted: ListaSubestipulanteItem[];
  subestipulantesPaginated: ListaSubestipulanteItem[];
  subestipulantesTotalPages: number;
  subestipulantesPage: number;
  setSubestipulantesPage: (page: number) => void;
}

export function useHomeContent(): UseHomeContentReturn {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const subestipulantes = useCompanyStore(
    (s) => s.selectedCompany?.subestipulantes ?? EMPTY_SUBESTIPULANTES,
  );

  const mainContentId = useId();
  const actionsRegionId = useId();
  const searchInputId = useId();
  const startDateId = useId();
  const endDateId = useId();
  const subestipulantesHeadingId = useId();
  const subestipulantesPanelId = useId();
  const subEstSearchInputId = useId();

  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [subestipulantesExpanded, setSubestipulantesExpanded] = useState(true);
  const [subestipulantesView, setSubestipulantesView] = useState<'cards' | 'table'>('cards');
  const [subestipulantesPage, setSubestipulantesPage] = useState(1);
  const [subEstSearchTerm, setSubEstSearchTerm] = useState('');
  const [subEstSortBy, setSubEstSortBy] = useState<string>('');

  const toggleSubestipulantesExpanded = useCallback(() => {
    setSubestipulantesExpanded((v) => !v);
  }, []);

  const toggleSubestipulantesView = useCallback(() => {
    setSubestipulantesView((v) => (v === 'cards' ? 'table' : 'cards'));
  }, []);

  const subestipulantesFilteredSorted = useMemo(() => {
    const term = subEstSearchTerm.trim();
    let list = subestipulantes;
    if (term) {
      list = list.filter((row) => subestipulanteMatchesSearch(row, term, formatDocSubestipulante));
    }
    return sortSubestipulantesList(list, subEstSortBy);
  }, [subEstSearchTerm, subEstSortBy, subestipulantes]);

  const subestipulantesTotalPages = useMemo(
    () => Math.max(1, Math.ceil(subestipulantesFilteredSorted.length / SUBESTIPULANTES_PAGE_SIZE)),
    [subestipulantesFilteredSorted.length],
  );

  useEffect(() => {
    setSubestipulantesPage(1);
  }, [subEstSearchTerm, subEstSortBy]);

  useEffect(() => {
    setSubestipulantesPage((p) => Math.min(p, subestipulantesTotalPages));
  }, [subestipulantesTotalPages]);

  const subestipulantesPaginated = useMemo(() => {
    const page = Math.min(Math.max(1, subestipulantesPage), subestipulantesTotalPages);
    const start = (page - 1) * SUBESTIPULANTES_PAGE_SIZE;
    return subestipulantesFilteredSorted.slice(start, start + SUBESTIPULANTES_PAGE_SIZE);
  }, [subestipulantesFilteredSorted, subestipulantesPage, subestipulantesTotalPages]);

  const isBuscarEnabled = useMemo(() => {
    if (!startDate || !endDate) return false;
    return new Date(startDate) <= new Date(endDate);
  }, [startDate, endDate]);

  const showInvalidDateMessage = useMemo(() => {
    if (!startDate || !endDate) return false;
    return new Date(startDate) > new Date(endDate);
  }, [startDate, endDate]);

  const handleBuscar = useCallback(() => {
    if (!isBuscarEnabled) return;
    // TODO: integrar busca com filtros (searchTerm, startDate, endDate)
  }, [isBuscarEnabled]);

  const handleNavigateToPoliceholder = useCallback(() => {
    navigate('/registerpoliceholder');
  }, [navigate]);

  return {
    isAdmin,
    subestipulantes,
    mainContentId,
    actionsRegionId,
    searchInputId,
    startDateId,
    endDateId,
    subestipulantesHeadingId,
    searchTerm,
    setSearchTerm,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isBuscarEnabled,
    showInvalidDateMessage,
    handleBuscar,
    handleNavigateToPoliceholder,
    subestipulantesPanelId,
    subEstSearchInputId,
    subestipulantesExpanded,
    toggleSubestipulantesExpanded,
    subestipulantesView,
    toggleSubestipulantesView,
    subEstSearchTerm,
    setSubEstSearchTerm,
    subEstSortBy,
    setSubEstSortBy,
    subestipulantesFilteredSorted,
    subestipulantesPaginated,
    subestipulantesTotalPages,
    subestipulantesPage,
    setSubestipulantesPage,
  };
}
